import { useEffect } from "react";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { useNotificationStore } from "../store/notificationStore";

export type ToastState = { message: string; type: "error" | "success" } | null;

interface ToastProps {
  message?: string | null;
  type?: "error" | "success";
  onDismiss: () => void;
}

const Toast = ({ message, type = "error", onDismiss }: ToastProps) => {
  useEffect(() => {
    if (!message) return;
    const id = setTimeout(onDismiss, 4000);
    return () => clearTimeout(id);
  }, [message, onDismiss]);

  if (!message) return null;

  const isError = type === "error";

  return (
    <div
      role="alert"
      className={`fixed right-4 top-4 z-50 flex w-full max-w-sm items-start gap-3 rounded-2xl border p-4 shadow-2xl backdrop-blur-xl ${
        isError
          ? "border-red-400/25 bg-[#1c0a0a]/90 text-red-200"
          : "border-green-400/25 bg-[#0a1c0a]/90 text-green-200"
      }`}
    >
      <span className="mt-0.5 shrink-0">
        {isError ? (
          <AlertCircle className="h-4 w-4" />
        ) : (
          <CheckCircle2 className="h-4 w-4" />
        )}
      </span>

      <p className="flex-1 text-sm leading-5">{message}</p>

      <button
        type="button"
        onClick={onDismiss}
        className={`shrink-0 rounded-lg p-0.5 transition ${
          isError ? "hover:bg-red-400/10" : "hover:bg-green-400/10"
        }`}
      >
        <X className="h-3.5 w-3.5 opacity-60" />
      </button>
    </div>
  );
};

export default Toast;

// Renders at the app root — reads from the global notification store.
// No local state needed in individual pages.
export const GlobalToast = () => {
  const { message, type, hide } = useNotificationStore();
  return <Toast message={message} type={type} onDismiss={hide} />;
};
